import React from 'react';
import { MessageSquare, Settings, Code, CheckCircle, Phone, ArrowRight } from 'lucide-react';
import { translations } from './languages';
import { useApp } from './AppContext';
const LandingPage = () => {

    const { currentLanguage } = useApp();
    const t = translations[currentLanguage];


    return (
        <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800">


            {/* Hero Section */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
                <div className="text-center">
                    <h1 className="text-5xl font-bold text-gray-900 dark:text-white mb-6">
                        {t.hero.title}
                    </h1>
                    <p className="text-xl text-gray-600 dark:text-gray-300 mb-8 max-w-2xl mx-auto">
                        {t.hero.subtitle}
                    </p>
                    <div className="flex justify-center space-x-4">
                        <button className="bg-indigo-600 dark:bg-indigo-500 text-white px-8 py-3 rounded-lg hover:bg-indigo-700 dark:hover:bg-indigo-600 flex items-center">
                            {t.hero.cta}
                            <ArrowRight className="ml-2 w-5 h-5" />
                        </button>
                    </div>
                </div>
            </div>

            {/* Setup Steps */}
            <div id="setup" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 bg-white dark:bg-gray-900">
                <h2 className="text-3xl font-bold text-center mb-12 dark:text-white">{t.setup.title}</h2>
                <div className="grid md:grid-cols-3 gap-8">
                    {t.setup.steps.map((step, index) => (
                        <div key={index} className="text-center p-6">
                            <div className="w-16 h-16 bg-indigo-100 dark:bg-indigo-900 rounded-full flex items-center justify-center mx-auto mb-4">
                                {index === 0 && <MessageSquare className="w-8 h-8 text-indigo-600 dark:text-indigo-400" />}
                                {index === 1 && <Settings className="w-8 h-8 text-indigo-600 dark:text-indigo-400" />}
                                {index === 2 && <Code className="w-8 h-8 text-indigo-600 dark:text-indigo-400" />}
                            </div>
                            <h3 className="text-xl font-semibold mb-4 dark:text-white">{step.title}</h3>
                            <p className="text-gray-600 dark:text-gray-300">{step.description}</p>
                        </div>
                    ))}
                </div>
            </div>

            {/* Pricing */}
            <div id="pricing" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 bg-gray-50 dark:bg-gray-800">
                <h2 className="text-3xl font-bold text-center mb-12 dark:text-white">{t.pricing.title}</h2>
                <div className="grid md:grid-cols-4 gap-8">
                    {Object.entries(t.pricing.plans).map(([plan, details]) => (
                        <div key={plan} className={`bg-white dark:bg-gray-900 p-8 rounded-lg shadow-lg ${plan === 'startup' ? 'border-2 border-indigo-600 dark:border-indigo-400' : ''
                            }`}>
                            <h3 className="text-xl font-semibold mb-4 dark:text-white">{details.name}</h3>
                            <div className="text-3xl font-bold mb-4 dark:text-white">
                                {details.price}<span className="text-gray-500 dark:text-gray-400 text-lg font-normal">{t.pricing.month}</span>
                            </div>
                            <ul className="space-y-4 mb-8">
                                {details.features.map((feature, index) => (
                                    <li key={index} className="flex items-center">
                                        <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
                                        <span className="dark:text-gray-300">{feature}</span>
                                    </li>
                                ))}
                            </ul>
                            <button className={`w-full ${plan === 'startup'
                                ? 'bg-indigo-600 dark:bg-indigo-500 text-white hover:bg-indigo-700 dark:hover:bg-indigo-600'
                                : 'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-white hover:bg-gray-200 dark:hover:bg-gray-700'
                                } py-2 rounded-lg flex items-center justify-center`}>
                                {plan === 'enterprise' && <Phone className="w-5 h-5 mr-2" />}
                                {plan === 'enterprise' ? t.pricing.contactSales : t.pricing.choosePlan}
                            </button>
                        </div>
                    ))}
                </div>
            </div>

            {/* Footer */}
            <footer className="bg-gray-900 dark:bg-gray-950 text-white py-12">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid md:grid-cols-4 gap-8">
                        <div>
                            <h4 className="text-lg font-semibold mb-4">zopai.cc</h4>
                            <p className="text-gray-400">{t.footer.description}</p>
                        </div>
                        <div>
                            <h4 className="text-lg font-semibold mb-4">{t.footer.product}</h4>
                            <ul className="space-y-2 text-gray-400">
                                <li><a href="#" className="hover:text-white">{t.nav.features}</a></li>
                                <li><a href="#" className="hover:text-white">{t.nav.pricing}</a></li>
                                <li><a href="#" className="hover:text-white">Documentation</a></li>
                            </ul>
                        </div>
                        <div>
                            <h4 className="text-lg font-semibold mb-4">{t.footer.company}</h4>
                            <ul className="space-y-2 text-gray-400">
                                <li><a href="#" className="hover:text-white">About</a></li>
                                <li><a href="#" className="hover:text-white">Blog</a></li>
                                <li><a href="#" className="hover:text-white">Contact</a></li>
                            </ul>
                        </div>
                        <div>
                            <h4 className="text-lg font-semibold mb-4">{t.footer.legal}</h4>
                            <ul className="space-y-2 text-gray-400">
                                <li><a href="#" className="hover:text-white">Privacy</a></li>
                                <li><a href="#" className="hover:text-white">Terms</a></li>
                            </ul>
                        </div>
                    </div>
                    <div className="border-t border-gray-800 mt-12 pt-8 text-center text-gray-400">
                        <p>&copy; 2025 zopai.cc. {t.footer.rights}</p>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default LandingPage;